/**
 * NotificationToneSettings
 *
 * Lets the user pick a tone per notification category and upload custom
 * audio files. The settings live on-device (localStorage + IndexedDB) —
 * see `utils/notificationTones.js` for the why and the API surface.
 *
 * UX:
 * - One row per category. Row shows: label, tone-source picker
 *   (Built-in / Custom / Silent), the selected tone, a Play button, and
 *   an Upload button when "Custom" is selected.
 * - All built-in tones from `BUILTIN_TONES` are previewable.
 * - Custom uploads show in a per-category dropdown so the user can pick
 *   from their library of uploaded files (one upload, reusable across
 *   categories).
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Play, Upload, Trash2, Volume2, VolumeX, Music, RefreshCw,
} from 'lucide-react';
import {
  NOTIFICATION_CATEGORIES,
  BUILTIN_TONES,
  loadPrefs,
  setCategoryPref,
  uploadCustomTone,
  listCustomTones,
  deleteCustomTone,
} from '../utils/notificationTones';
import { useNotificationSound } from '../contexts/NotificationSoundContext';

export default function NotificationToneSettings() {
  const { previewTone } = useNotificationSound();
  const [prefs, setPrefs] = useState(() => loadPrefs());
  const [customTones, setCustomTones] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Refresh the custom-tone list from IndexedDB. Called on mount and any
  // time the user uploads or deletes a custom tone.
  const refreshCustomList = async () => {
    setRefreshing(true);
    try {
      const items = await listCustomTones();
      setCustomTones(items.sort((a, b) => b.savedAt - a.savedAt));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    refreshCustomList();
  }, []);

  // Mutating helper — write a category pref and reflect it locally so
  // the UI updates without waiting for a localStorage re-read.
  const updatePref = (categoryKey, pref) => {
    setCategoryPref(categoryKey, pref);
    setPrefs((prev) => ({ ...prev, [categoryKey]: pref }));
  };

  const handleSourceChange = (categoryKey, source) => {
    const cat = NOTIFICATION_CATEGORIES.find((c) => c.key === categoryKey);
    if (source === 'builtin') {
      const current = prefs[categoryKey];
      updatePref(categoryKey, {
        kind: 'builtin',
        builtinId: current?.builtinId || cat?.defaultBuiltin || 'chime',
      });
    } else if (source === 'custom') {
      // Default to the first available custom tone if any; otherwise
      // leave customId empty and let the user upload.
      updatePref(categoryKey, {
        kind: 'custom',
        customId: customTones[0]?.id || null,
      });
    } else {
      updatePref(categoryKey, { kind: 'silent' });
    }
  };

  const handleBuiltinChange = (categoryKey, builtinId) => {
    updatePref(categoryKey, { kind: 'builtin', builtinId });
  };

  const handleCustomChange = (categoryKey, customId) => {
    updatePref(categoryKey, { kind: 'custom', customId });
  };

  const handleUpload = async (categoryKey, file) => {
    try {
      const { id, filename } = await uploadCustomTone(file);
      await refreshCustomList();
      updatePref(categoryKey, { kind: 'custom', customId: id });
      toast.success(`Uploaded "${filename}" — assigned to ${
        NOTIFICATION_CATEGORIES.find((c) => c.key === categoryKey)?.label
      }`);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    }
  };

  const handleDeleteCustom = async (id) => {
    await deleteCustomTone(id);
    await refreshCustomList();
    // Any category that pointed at this tone now has an orphan ref —
    // reset those to the category default so playback never silently
    // misses.
    const updated = { ...prefs };
    let changed = false;
    for (const cat of NOTIFICATION_CATEGORIES) {
      const p = updated[cat.key];
      if (p?.kind === 'custom' && p.customId === id) {
        updated[cat.key] = { kind: 'builtin', builtinId: cat.defaultBuiltin };
        setCategoryPref(cat.key, updated[cat.key]);
        changed = true;
      }
    }
    if (changed) setPrefs(updated);
    toast.success('Tone removed');
  };

  return (
    <div className="space-y-6">
      {/* Per-category list. Each row is self-contained so the user can
          configure one category at a time without committing. */}
      {NOTIFICATION_CATEGORIES.map((cat) => {
        const pref = prefs[cat.key] || { kind: 'builtin', builtinId: cat.defaultBuiltin };
        return (
          <Card key={cat.key}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mt-0.5">
                    {pref.kind === 'silent'
                      ? <VolumeX className="h-5 w-5 text-muted-foreground" />
                      : <Volume2 className="h-5 w-5 text-primary" />}
                  </div>
                  <div>
                    <CardTitle className="text-base">{cat.label}</CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      {cat.description}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Always-visible Upload button. Clicking opens the
                      file picker; on success we both add the file to the
                      library AND auto-switch this category to use it,
                      so the user doesn't have to change the dropdown
                      separately. */}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUpload(cat.key, f);
                        e.target.value = '';
                      }}
                    />
                    <span className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent">
                      <Upload size={13} className="mr-1.5" /> Upload tone
                    </span>
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => previewTone(pref)}
                    // Disable when there's nothing meaningful to play —
                    // silent category, or custom selected with no file
                    // picked yet (would just be a confused click).
                    disabled={
                      pref.kind === 'silent'
                      || (pref.kind === 'custom' && !pref.customId)
                    }
                    title={pref.kind === 'custom' && !pref.customId
                      ? 'Upload a file or pick one from the library to enable preview'
                      : 'Play this tone'}
                    data-testid={`tone-preview-${cat.key}`}
                  >
                    <Play size={13} className="mr-1.5" /> Play
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {/* Source picker — built-in / custom / silent */}
                <Select
                  value={pref.kind}
                  onValueChange={(v) => handleSourceChange(cat.key, v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="builtin">Built-in tone</SelectItem>
                    <SelectItem value="custom">Custom upload</SelectItem>
                    <SelectItem value="silent">Silent (no sound)</SelectItem>
                  </SelectContent>
                </Select>

                {/* When built-in, show the tone picker */}
                {pref.kind === 'builtin' && (
                  <Select
                    value={pref.builtinId}
                    onValueChange={(v) => handleBuiltinChange(cat.key, v)}
                  >
                    <SelectTrigger className="sm:col-span-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BUILTIN_TONES.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label} — <span className="text-muted-foreground">{t.description}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* When custom, show a dropdown of uploaded tones + an
                    Upload button on the same row. */}
                {pref.kind === 'custom' && (
                  <div className="sm:col-span-2 flex gap-2">
                    <Select
                      value={pref.customId || ''}
                      onValueChange={(v) => handleCustomChange(cat.key, v)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={customTones.length ? 'Choose a tone' : 'No custom tones — upload one'} />
                      </SelectTrigger>
                      <SelectContent>
                        {customTones.length === 0 && (
                          <div className="px-2 py-2 text-xs text-muted-foreground">
                            No custom tones uploaded yet
                          </div>
                        )}
                        {customTones.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            <span className="flex items-center gap-2">
                              <Music size={12} />
                              {t.filename}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUpload(cat.key, f);
                          e.target.value = ''; // allow re-selecting the same file
                        }}
                      />
                      <span className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent">
                        <Upload size={13} className="mr-1.5" /> Upload
                      </span>
                    </label>
                  </div>
                )}

                {pref.kind === 'silent' && (
                  <div className="sm:col-span-2 flex items-center text-xs text-muted-foreground px-3 py-2">
                    Notifications in this category arrive silently (no sound).
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Library — list every uploaded tone with a delete button. Lets
          the user clean up files they no longer need, and shows what
          they currently have available across all categories. */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Music size={18} /> Custom Tone Library
                {customTones.length > 0 && (
                  <Badge variant="outline" className="text-xs">{customTones.length}</Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Audio files uploaded from this device. Up to 2 MB per file. Stored locally — uploads don't sync across devices.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Library-level upload — adds to the library without
                  assigning to any category. Useful for stocking the
                  library first, then picking per-category from the
                  dropdowns. */}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      try {
                        const { id, filename } = await uploadCustomTone(f);
                        await refreshCustomList();
                        toast.success(`Uploaded "${filename}" to library`);
                      } catch (err) {
                        toast.error(err.message || 'Upload failed');
                      }
                    }
                    e.target.value = '';
                  }}
                />
                <span className="inline-flex h-9 items-center justify-center rounded-md bg-primary text-primary-foreground px-3 text-sm font-medium hover:bg-primary/90">
                  <Upload size={13} className="mr-1.5" /> Upload new tone
                </span>
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshCustomList}
                disabled={refreshing}
              >
                <RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {customTones.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No custom tones yet. Choose "Custom upload" on any category above to add one.
            </p>
          ) : (
            <div className="space-y-2">
              {customTones.map((t) => {
                // Find every category currently using this tone so the
                // user knows the impact of deleting it.
                const usedBy = NOTIFICATION_CATEGORIES.filter(
                  (c) => prefs[c.key]?.kind === 'custom' && prefs[c.key]?.customId === t.id
                );
                return (
                  <div key={t.id} className="flex items-center gap-3 p-2 rounded border border-border">
                    <Music size={14} className="text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{t.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        Uploaded {new Date(t.savedAt).toLocaleString()}
                        {usedBy.length > 0 && (
                          <> • In use by: {usedBy.map((c) => c.label).join(', ')}</>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => previewTone({ kind: 'custom', customId: t.id })}
                    >
                      <Play size={13} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteCustom(t.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
